/**
 * Magazine Banner Card
 * 
 * A cinematic, magazine-cover inspired project card that transforms
 * property development listings into editorial masterpieces.
 * 
 * Features:
 * - Full-bleed background imagery
 * - Editorial typography with dramatic hierarchy
 * - Sophisticated gradient overlays
 * - Elegant hover animations
 * - Magazine cover aesthetic
 * - Responsive banner proportions
 */

'use client';

import React, { useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import { CURRENCY_CONFIG, DEFAULT_CURRENCY, type Currency } from '@/lib/config/app.config';

export interface MagazineBannerCardProps {
  project: PropertyDevelopmentProject;
  href: string;
  brandLogo?: string;
  brandColors?: {
    primary?: string;
    accent?: string;
  };
  messages: {
    availableUnits: string;
    soldOut: string;
    comingSoon: string;
    enquireNow: string;
    viewProject: string;
    startingFrom: string;
    explore?: string;
  };
  locale?: string;
  className?: string;
  variant?: 'default' | 'featured' | 'compact';
}

/**
 * MagazineBannerCard - Editorial magazine cover style project card
 */
export function MagazineBannerCard({
  project,
  href,
  brandLogo,
  brandColors,
  messages,
  locale: _locale = 'en',
  className = '',
  variant = 'default',
}: MagazineBannerCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const currencyContext = useCurrencyOptional();

  // Subtle mouse tracking for parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Spring physics for smooth movement
  const springConfig = { stiffness: 150, damping: 20 };
  const x = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), springConfig);
  const y = useSpring(useTransform(mouseY, [-0.5, 0.5], [-8, 8]), springConfig);
  const scale = useSpring(isHovered ? 1.02 : 1, springConfig);

  const colors = {
    primary: brandColors?.primary || '#1a1a2e',
    accent: brandColors?.accent || '#d4af37',
  };

  const isSoldOut = project.status === 'SOLD_OUT' || project.availableUnits === 0;
  const isComingSoon = project.status === 'UPCOMING';
  const isActive = project.status === 'ACTIVE' && !isSoldOut;

  // Calculate availability
  const availabilityPercentage = project.totalUnits && project.soldUnits
    ? Math.round(((project.totalUnits - project.soldUnits) / project.totalUnits) * 100)
    : 100;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    mouseX.set((e.clientX - centerX) / (rect.width / 2));
    mouseY.set((e.clientY - centerY) / (rect.height / 2));
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    mouseX.set(0);
    mouseY.set(0);
  };

  // Height based on variant
  const heightClasses = {
    default: 'h-[520px] md:h-[580px]',
    featured: 'h-[600px] md:h-[700px]',
    compact: 'h-[400px] md:h-[450px]',
  };

  return (
    <Link href={href} className={`block ${className}`}>
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{ scale }}
        className={`
          relative overflow-hidden rounded-2xl cursor-pointer
          ${heightClasses[variant]}
          shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)]
          transition-shadow duration-700
          hover:shadow-[0_25px_60px_-12px_rgba(0,0,0,0.4)]
        `}
      >
        {/* Background Image with Parallax */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{ x, y }}
        >
          {project.heroImageUrl ? (
            <Image
              src={project.heroImageUrl}
              alt={project.name}
              fill
              className="object-cover transition-transform duration-1000 ease-out"
              style={{ transform: isHovered ? 'scale(1.08)' : 'scale(1)' }}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
              priority
            />
          ) : (
            <div
              className="h-full w-full"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent}40 50%, ${colors.primary} 100%)`,
              }}
            />
          )}
        </motion.div>

        {/* Cinematic Gradient Overlays */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/60 via-transparent to-black/30" />
        
        {/* Vignette Effect */}
        <div 
          className="absolute inset-0 z-[1] opacity-40"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 100%)',
          }}
        />

        {/* Magazine Header Bar */}
        <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between p-6 md:p-8">
          {/* Brand Logo / Issue Badge */}
          {brandLogo ? (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-xl bg-white/95 p-3 shadow-2xl backdrop-blur-md"
            >
              <Image
                src={brandLogo}
                alt="Brand"
                width={90}
                height={36}
                className="h-auto w-20 object-contain"
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-2"
            >
              <div 
                className="h-1.5 w-8 rounded-full"
                style={{ backgroundColor: colors.accent }}
              />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/90">
                {project.projectType?.replace(/_/g, ' ') || 'Development'}
              </span>
            </motion.div>
          )}

          {/* Status Badge - Magazine Style */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            {isSoldOut ? (
              <div className="relative">
                <div className="rounded-full bg-gradient-to-r from-red-700 to-red-600 px-5 py-2 shadow-lg">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-white">
                    {messages.soldOut}
                  </span>
                </div>
              </div>
            ) : isComingSoon ? (
              <div 
                className="rounded-full px-5 py-2 shadow-lg backdrop-blur-md"
                style={{ backgroundColor: `${colors.accent}ee` }}
              >
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-white">
                  {messages.comingSoon}
                </span>
              </div>
            ) : isActive ? (
              <div className="flex items-center gap-2 rounded-full bg-emerald-600/95 px-5 py-2 shadow-lg backdrop-blur-md">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                </span>
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-white">
                  {project.availableUnits} {messages.availableUnits}
                </span>
              </div>
            ) : null}
          </motion.div>
        </div>

        {/* Main Content - Editorial Layout */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-6 md:p-8 lg:p-10">
          {/* Category Line */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-4 flex items-center gap-3"
          >
            <div 
              className="h-px flex-shrink-0 w-12"
              style={{ backgroundColor: colors.accent }}
            />
            <span 
              className="text-xs font-semibold uppercase tracking-[0.3em]"
              style={{ color: colors.accent }}
            >
              {project.stage?.replace(/_/g, ' ') || 'New Development'}
            </span>
          </motion.div>

          {/* Project Title - Magazine Headline Style */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-editorial mb-4 text-3xl font-black leading-[1.1] tracking-tight text-white md:text-4xl lg:text-5xl"
            style={{ 
              fontFamily: "var(--font-editorial, 'Playfair Display', Georgia, serif)",
              textShadow: '0 4px 20px rgba(0,0,0,0.4)',
            }}
          >
            {project.name}
          </motion.h2>

          {/* Description - Subheadline */}
          {project.description && (
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-6 line-clamp-2 max-w-xl text-base leading-relaxed text-white/80 md:text-lg"
            >
              {project.description}
            </motion.p>
          )}

          {/* Stats Bar - Magazine Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-6 flex flex-wrap items-center gap-6 border-t border-white/20 pt-6"
          >
            {/* Price Feature - currency-aware */}
            {(project.displayPriceAmount || project.displayPriceText) && (
              <div className="flex flex-col">
                <span className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/60">
                  {messages.startingFrom}
                </span>
                <span 
                  className="text-2xl font-bold md:text-3xl"
                  style={{ color: colors.accent }}
                >
                  {(() => {
                    if (!project.displayPriceAmount || !project.displayPriceCurrency) {
                      return project.displayPriceText;
                    }
                    const selectedCurrency = currencyContext?.selectedCurrency ?? DEFAULT_CURRENCY;
                    const originalCurrency = project.displayPriceCurrency.toUpperCase();
                    let displayAmount = project.displayPriceAmount;
                    let displayCurrency = originalCurrency;

                    if (originalCurrency !== selectedCurrency && currencyContext?.rates) {
                      const converted = currencyContext.convertBetween(
                        project.displayPriceAmount,
                        originalCurrency,
                        selectedCurrency
                      );
                      if (converted !== null) {
                        displayAmount = converted;
                        displayCurrency = selectedCurrency;
                      }
                    }

                    const config = CURRENCY_CONFIG[displayCurrency as Currency];
                    if (!config) return project.displayPriceText;
                    try {
                      return new Intl.NumberFormat(config.locale, {
                        style: 'currency',
                        currency: displayCurrency,
                        minimumFractionDigits: config.decimalPlaces,
                        maximumFractionDigits: config.decimalPlaces,
                      }).format(displayAmount);
                    } catch {
                      return project.displayPriceText;
                    }
                  })()}
                </span>
              </div>
            )}

            {/* Divider */}
            {project.displayPriceText && project.totalUnits && (
              <div className="h-12 w-px bg-white/20" />
            )}

            {/* Units */}
            {project.totalUnits && (
              <div className="flex flex-col">
                <span className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/60">
                  Total Units
                </span>
                <span className="text-2xl font-bold text-white md:text-3xl">
                  {project.totalUnits}
                </span>
              </div>
            )}

            {/* Availability Indicator */}
            {!isSoldOut && project.totalUnits && (
              <>
                <div className="h-12 w-px bg-white/20" />
                <div className="flex flex-col">
                  <span className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/60">
                    Available
                  </span>
                  <div className="flex items-baseline gap-1">
                    <span 
                      className="text-2xl font-bold md:text-3xl"
                      style={{ color: availabilityPercentage > 50 ? '#10b981' : colors.accent }}
                    >
                      {availabilityPercentage}%
                    </span>
                  </div>
                </div>
              </>
            )}
          </motion.div>

          {/* CTA Button - Magazine Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex items-center gap-4"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group/btn relative overflow-hidden rounded-full px-8 py-3.5"
              style={{ backgroundColor: colors.accent }}
            >
              {/* Shine effect on hover */}
              <div 
                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover/btn:translate-x-full"
              />
              <span className="relative flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white">
                {messages.explore || messages.viewProject}
                <motion.svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  animate={{ x: isHovered ? 4 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </motion.svg>
              </span>
            </motion.div>

            {/* Secondary Action */}
            <span className="hidden text-sm font-medium text-white/60 transition-colors hover:text-white md:inline-flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Save
            </span>
          </motion.div>
        </div>

        {/* Corner Accent - Magazine Page Curl Effect */}
        <motion.div
          className="absolute bottom-0 right-0 z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
            <path
              d="M60 0 L60 60 L0 60 Z"
              fill={`${colors.accent}40`}
            />
            <path
              d="M60 15 L60 60 L15 60 Z"
              fill={`${colors.accent}60`}
            />
          </svg>
        </motion.div>

        {/* Animated Border on Hover */}
        <motion.div
          className="pointer-events-none absolute inset-0 z-30 rounded-2xl"
          style={{
            border: `2px solid transparent`,
            borderImage: `linear-gradient(135deg, ${colors.accent}00, ${colors.accent}, ${colors.accent}00) 1`,
          }}
          animate={{
            opacity: isHovered ? 0.6 : 0,
          }}
          transition={{ duration: 0.4 }}
        />
      </motion.div>
    </Link>
  );
}
